import React from 'react';
import { getEmojiName } from './emojiNames';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/**
 * Matches a single emoji grapheme, including ZWJ sequences (e.g. family
 * emoji), skin tone modifiers, regional indicator pairs (flags) and keycaps.
 *
 * Only codepoints with emoji presentation are matched: text-default
 * pictographs such as U+2122 are matched exclusively when followed by the
 * emoji variation selector U+FE0F, so ordinary typographic characters are not
 * rescaled. This mirrors the unicode-range of the emoji @font-face in
 * WebResourceProviderHook/WebContent/WEB-INF/fonts.jsp.
 */
const EMOJI_ATOM =
  '(?:\\p{Emoji_Presentation}|\\p{Extended_Pictographic}\\uFE0F)' +
  '(?:\\p{Emoji_Modifier}|\\uFE0F)?';

const EMOJI_PATTERN = new RegExp(
  `((?:[#*0-9]\\uFE0F?\\u20E3|\\p{Regional_Indicator}{2}|${EMOJI_ATOM})` +
    `(?:\\u200D${EMOJI_ATOM})*)`,
  'gu'
);

export const SCALED_EMOJI_CLASS = 'ehs-scaled-emoji';

/**
 * Splits `text` into alternating plain and emoji segments.
 */
export function emojiSegments(
  text: string
): { text: string; isEmoji: boolean }[] {
  EMOJI_PATTERN.lastIndex = 0;
  return (
    text
      .split(EMOJI_PATTERN)
      // split() with a capturing group yields the captured separators at odd
      // indices, so these are exactly the emoji matches.
      .map((segment, index) => ({ text: segment, isEmoji: index % 2 === 1 }))
      .filter(segment => segment.text !== '')
  );
}

/**
 * Wraps every emoji in `text` into a span so it can be scaled independently of
 * the surrounding copy and announced by its name instead of its codepoint.
 *
 * The `size-adjust` descriptor cannot be used for this: Chromium only applies
 * it to fonts it loads itself, not to `local()` system emoji fonts, which would
 * make emoji render at different sizes in Chromium and Gecko.
 *
 * Returns the input unchanged when it contains no emoji, so the common case
 * allocates nothing.
 */
export function withScaledEmoji(
  text: string,
  emojiData: EmojiData
): React.ReactNode {
  EMOJI_PATTERN.lastIndex = 0;
  if (!EMOJI_PATTERN.test(text)) {
    return text;
  }

  return emojiSegments(text).map((segment, index): React.ReactNode | string => {
    if (!segment.isEmoji) return segment.text;
    const name = getEmojiName(segment.text, emojiData);
    return (
      <span
        key={index}
        className={SCALED_EMOJI_CLASS}
        role={name ? 'img' : undefined}
        aria-label={name}
      >
        {segment.text}
      </span>
    );
  });
}

/**
 * DOM equivalent of {@link withScaledEmoji} for a single emoji, for consumers
 * that cannot render React nodes, e.g. a `contenteditable` host.
 *
 * The span is marked `contenteditable="false"`, which makes it an atomic unit
 * inside an editable host: Chromium otherwise treats a caret placed behind the
 * span as being inside it and pulls subsequently typed text into it, where it
 * would be rendered at emoji size. It also makes the emoji delete in one step
 * instead of leaving parts of a ZWJ sequence behind.
 *
 * `name` overrides the looked up name, e.g. with the one an emoji picker
 * already provides.
 */
export function scaledEmojiSpan(
  emoji: string,
  emojiData?: EmojiData,
  name?: string
): HTMLElement {
  const span = document.createElement('span');
  span.className = SCALED_EMOJI_CLASS;
  span.contentEditable = 'false';
  span.textContent = emoji;
  const label =
    name ?? getEmojiName(emoji, emojiData ?? { categories: {}, emojis: {} });
  if (label) {
    span.setAttribute('role', 'img');
    span.setAttribute('aria-label', label);
  }
  return span;
}

/**
 * DOM equivalent of {@link withScaledEmoji}: the same markup, built as plain
 * nodes so nothing in `text` can be interpreted as markup.
 */
export function scaledEmojiNodes(text: string, emojiData: EmojiData): Node[] {
  return emojiSegments(text).map(segment =>
    segment.isEmoji
      ? scaledEmojiSpan(segment.text, emojiData)
      : document.createTextNode(segment.text)
  );
}
